'use client';

import React, { useRef, useEffect } from 'react';

interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Applied to the first box, so an external <label htmlFor> lands somewhere. */
  id?: string;
  /** Validation message; marks every box invalid and points them at it. */
  error?: string;
}

export function VerificationCodeInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = false,
  id,
  error,
}: VerificationCodeInputProps) {
  const errorId = id ? `${id}-error` : undefined;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);

    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [length, autoFocus]);

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;

    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const valueArray = value.split('');
    valueArray[index] = newValue.slice(-1);
    const newCode = valueArray.join('').slice(0, length);
    onChange(newCode);

    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const numericData = pastedData.replace(/\D/g, '').slice(0, length);

    if (numericData) {
      onChange(numericData);
      const nextIndex = Math.min(numericData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-center gap-2">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            id={index === 0 ? id : undefined}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={value[index] || ''}
            onChange={e => handleChange(index, e)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={`h-12 w-11 rounded-md border text-center text-lg font-semibold shadow-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 bg-surface text-on-surface focus:ring-accent focus:border-accent xs:w-12 ${
              error ? 'border-danger' : 'border-secondary'
            }`}
            aria-label={`Verification code digit ${index + 1}`}
          />
        ))}
      </div>
      {error && (
        <p id={errorId} role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export default VerificationCodeInput;
